// Category-Feed — Route `/category/:id`.
//
// Phase 11 Step 4 Block 2 (Routing): Minimal-Implementierung.
// Filtert `SCROLLYS` nach `categoryId` und rendert sie in einem
// `CardGrid`. Kein Hero, keine kurativen Rows — eine Kategorie hat
// eine Vorauswahl, nicht ein komplettes Feed-Layout.
//
// Phase 11 Step 5 Block 0 (Strict 404): Unbekannte Kategorien rendern
// die geteilte `NotFound`-Komponente statt einer inline Fehlermeldung.
//
// `CategoryChips` erscheint oben und markiert die aktuelle Kategorie
// über `pageContext.urlPathname` (siehe CategoryChips.tsx).

import { AppShell } from "../../../src/components/layout/AppShell";
import { CardGrid } from "../../../src/components/feed/CardGrid";
import { CategoryChips } from "../../../src/components/feed/CategoryChips";
import { ScrollyCard } from "../../../src/components/feed/ScrollyCard";
import { SectionTitle } from "../../../src/components/feed/SectionTitle";
import { NotFound } from "../../../src/components/common/NotFound";
import {
  CATEGORIES,
  CATEGORY_BY_ID,
  type CategoryId,
} from "../../../src/lib/categories";
import { FONT_DISPLAY, PINK, VARIANT } from "../../../src/lib/tokens";
import { useVariant } from "../../../src/hooks/useVariant";
import { usePageContext } from "../../../src/hooks/usePageContext";
import { scrollys as ALL_SCROLLYS } from "../../../src/lib/mock/scrollys";

export default function Page() {
  const { variant } = useVariant();
  const v = VARIANT[variant];
  const { routeParams } = usePageContext();
  const rawId = decodeURIComponent(routeParams?.id ?? "");
  const isKnown = CATEGORIES.some((c) => c.id === rawId);

  if (!isKnown) {
    return (
      <NotFound
        eyebrow="Kategorie nicht gefunden"
        headline={
          <>
            Keine Kategorie <em style={{ color: PINK, fontStyle: "italic" }}>{rawId}</em>.
          </>
        }
        detail="Diese Kategorie gibt es im aktuellen Mock-Set nicht."
      />
    );
  }

  const categoryId = rawId as CategoryId;
  const category = CATEGORY_BY_ID[categoryId];
  const scrollysInCategory = ALL_SCROLLYS.filter((s) => s.categoryId === categoryId);

  return (
    <AppShell currentNavId="" loggedIn={false}>
      <div style={{ background: v.bg, minHeight: "100%" }}>
        <CategoryChips />
        <div
          style={{
            padding: "32px 32px 80px",
            maxWidth: 1600,
            margin: "0 auto",
          }}
        >
          <div style={{ marginBottom: 24 }}>
            <div
              style={{
                fontSize: 11,
                fontWeight: 700,
                letterSpacing: 1.2,
                textTransform: "uppercase",
                color: PINK,
                marginBottom: 8,
              }}
            >
              Kategorie
            </div>
            <h1
              style={{
                fontFamily: FONT_DISPLAY,
                fontWeight: 500,
                fontSize: 52,
                margin: 0,
                letterSpacing: -0.8,
                color: v.text,
              }}
            >
              {category.label}
            </h1>
            <p
              style={{
                fontSize: 14,
                color: v.muted,
                marginTop: 8,
              }}
            >
              {scrollysInCategory.length} Scrollys in dieser Kategorie.
            </p>
          </div>

          {scrollysInCategory.length > 0 ? (
            <>
              <SectionTitle
                title="Alle Scrollys"
                subtitle={`Ausgewählt aus der Kategorie "${category.label}"`}
              />
              <CardGrid>
                {scrollysInCategory.map((s) => (
                  <ScrollyCard key={s.id} scrolly={s} />
                ))}
              </CardGrid>
            </>
          ) : (
            <p
              style={{
                color: v.muted,
                fontSize: 14,
                marginTop: 32,
              }}
            >
              Noch keine Scrollys in dieser Kategorie verfügbar.
            </p>
          )}

          <a
            href="/"
            style={{
              display: "inline-block",
              marginTop: 40,
              color: PINK,
              fontSize: 14,
              fontWeight: 600,
              textDecoration: "none",
            }}
          >
            ← Zurück zum Feed
          </a>
        </div>
      </div>
    </AppShell>
  );
}
